// How each 3D unit moves while it lives. The infantry play the clips their model carries
// (aim, walk, run, shoot on the move, die); everything else is animated in code: rotors and
// propellers spin, the helicopter and drones hover, pitch into their speed and bank into
// turns, the tank's turret swings onto its target and the hull rocks back when it fires,
// boats ride the swell and lift their bows, the cruise missile rolls on a flame.
// The scene positions and turns each unit's group; the motion here only moves the model
// inside it, so the two never fight.
import * as THREE from 'three';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { CrowdMember } from './crowd3d.ts';

/** A copy of a loaded model to give one unit: skinned models need their skeleton copied too. */
export function cloneModel(model: THREE.Object3D): THREE.Object3D {
  let skinned = false;
  model.traverse(o => { if ((o as THREE.SkinnedMesh).isSkinnedMesh) skinned = true; });
  const copy = skinned ? cloneSkinned(model) : model.clone();
  if (skinned) shareSkeletons(copy);
  copy.animations = model.animations;
  return copy;
}

/**
 * The loader gives every skinned part of a model (a soldier's body, head, legs: nine of them) its
 * own copy of the one skeleton, and the renderer works out and uploads each copy's bones once per
 * pass. Parts bound to the same bones the same way take the first part's skeleton instead.
 */
function shareSkeletons(root: THREE.Object3D): void {
  const kept: THREE.Skeleton[] = [];
  root.traverse(o => {
    const mesh = o as THREE.SkinnedMesh;
    if (!mesh.isSkinnedMesh) return;
    const same = kept.find(s => sameSkeleton(s, mesh.skeleton));
    if (same) mesh.skeleton = same;
    else kept.push(mesh.skeleton);
  });
}

/**
 * Joins a loaded model's skinned parts that share a material, a placing and a skeleton into one
 * mesh, so a soldier draws in four calls instead of nine in each pass.
 */
export function mergeSkinnedParts(root: THREE.Object3D): void {
  const sets = new Map<string, THREE.SkinnedMesh[]>();
  root.updateMatrixWorld(true);
  root.traverse(o => {
    const m = o as THREE.SkinnedMesh;
    if (!m.isSkinnedMesh || Array.isArray(m.material)) return;
    const key = m.material.uuid;
    let set = sets.get(key);
    if (!set) sets.set(key, set = []);
    set.push(m);
  });
  for (const parts of sets.values()) {
    const first = parts[0]!;
    if (parts.length < 2) continue;
    if (!parts.every(p => sameSkeleton(p.skeleton, first.skeleton) && p.bindMatrix.equals(first.bindMatrix) && p.matrixWorld.equals(first.matrixWorld))) continue;
    const geo = mergeGeometries(parts.map(p => p.geometry));
    if (!geo) continue;
    geo.userData.shared = true;
    first.geometry = geo;
    for (const p of parts.slice(1)) p.removeFromParent();
  }
}

function sameSkeleton(a: THREE.Skeleton, b: THREE.Skeleton): boolean {
  if (a.bones.length !== b.bones.length) return false;
  for (let i = 0; i < a.bones.length; i++) {
    if (a.bones[i] !== b.bones[i]) return false;
    const x = a.boneInverses[i]!.elements, y = b.boneInverses[i]!.elements;
    for (let j = 0; j < 16; j++) if (Math.abs(x[j]! - y[j]!) > 1e-6) return false;
  }
  return true;
}

/** What the scene knows about a unit this frame. */
export interface MotionInput {
  /** Milliseconds. */
  now: number;
  /** Seconds since the last frame. */
  dt: number;
  /** Ground speed, tiles a second. */
  speed: number;
  /** Turn rate, radians a second, positive to the left. */
  turn: number;
  /** Where its latest shot went, as an angle in the unit's own frame (0 dead ahead, positive to the left), or null when it is not firing. */
  aim: number | null;
  /** A shot has just left the gun this frame. */
  fired: boolean;
}

type Kind = 'foot' | 'vehicle' | 'ship' | 'air' | 'drone' | 'missile';

/** Clip names, without the rig's prefix. */
const CLIP = {
  idle: 'Idle_Gun', aim: 'Idle_Gun_Pointing', shoot: 'Idle_Gun_Shoot', walk: 'Walk', run: 'Run', runShoot: 'Run_Shoot', die: 'Death',
} as const;
/** Missiles drawn flying on a flame: the cruise missiles, and the EMP's carrier. */
const FLAME = new Set(['cruise_missile', 'kalibr', 'emp_missile']);
/** Fixed-wing drones: they bank hard and barely bob; the quadcopters hover and tilt into their speed. */
const WINGED = new Set(['attack_drone', 'shahed']);
/** Faster than this (tiles a second) a soldier runs; slower, he walks. */
const RUN_FROM = 1.4;
const FADE = 0.22;
/** How long a soldier stays aimed after his last shot before lowering the gun, ms. */
const HOLD_AIM = 900;

export class UnitMotion {
  private readonly mixer: THREE.AnimationMixer | null = null;
  private readonly actions = new Map<string, THREE.AnimationAction>();
  private current: THREE.AnimationAction | null = null;
  private readonly spinners: { node: THREE.Object3D; axis: 'x' | 'y' | 'z'; rate: number }[] = [];
  private readonly turret: THREE.Object3D | null;
  private readonly turretBase: THREE.Vector3 | null;
  private readonly barrel: THREE.Object3D | null;
  private readonly barrelBase: THREE.Vector3 | null;
  private readonly flame: THREE.Mesh | null = null;
  private readonly phase: number;
  private kick = 0;
  private barrelKick = 0;
  private bank = 0;
  private pitch = 0;
  private lastSpeed = 0;
  private lastShot = -Infinity;
  private dead = false;

  /**
   * `crowd` is given for a soldier drawn in a crowd (crowd3d.ts): his clips play there, in the
   * shader, and the mixer is not made; the model is then only the group that places him.
   */
  constructor(private readonly model: THREE.Object3D, private readonly kind: Kind, private readonly type: string, seed: number, private readonly crowd: CrowdMember | null = null) {
    this.phase = (Math.sin(seed * 12.9898) * 43758.5453) % 1;
    if (crowd) {
      this.play(CLIP.idle, 0);
      // squads don't move in lockstep
      crowd.skip(Math.abs(this.phase) * 3);
    } else if (model.animations.length) {
      this.mixer = new THREE.AnimationMixer(model);
      for (const clip of model.animations) this.actions.set(clip.name.split('|').pop()!, this.mixer.clipAction(clip));
      this.play(CLIP.idle, 0);
      // squads don't move in lockstep
      this.mixer.update(Math.abs(this.phase) * 3);
    }
    // a helicopter's main and tail rotors (the Ka-52's lower rotor turns the other way), a quadcopter's four,
    // the Shahed's pusher propeller, the Zubr's three fans, and a warship's mast radar
    for (const [name, axis, rate] of [['rotor', 'y', 38], ['rotor_lower', 'y', -38], ['tail', 'z', 55], ['rotor0', 'y', 70], ['rotor1', 'y', -70], ['rotor2', 'y', -70], ['rotor3', 'y', 70],
      ['prop', 'x', 60], ['fan0', 'x', 45], ['fan1', 'x', -45], ['fan2', 'x', 45], ['radar', 'y', 2.8]] as const) {
      const node = model.getObjectByName(name);
      if (node) this.spinners.push({ node, axis, rate });
    }
    this.turret = model.getObjectByName('turret') ?? null;
    this.turretBase = this.turret?.position.clone() ?? null;
    this.barrel = model.getObjectByName('barrel') ?? null;
    if (this.turret && this.barrel && this.barrel.parent !== this.turret) {
      this.barrel.position.sub(this.turret.position);
      this.turret.add(this.barrel);
    }
    this.barrelBase = this.barrel?.position.clone() ?? null;
    if (FLAME.has(type)) {
      // a flame out of the tail, bright enough to bloom
      model.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(model);
      const geo = new THREE.ConeGeometry(0.035, 0.22, 8, 1, true);
      geo.rotateZ(Math.PI / 2);
      geo.translate(-0.11, 0, 0);
      this.flame = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: new THREE.Color(0xffb050).multiplyScalar(4), transparent: true, opacity: 0.85, depthWrite: false }));
      this.flame.position.set(box.min.x / model.scale.x, (box.min.y + box.max.y) / 2 / model.scale.y, 0);
      model.add(this.flame);
    }
  }

  /** True once the unit has been told it died and its last animation has played out. */
  get gone(): boolean {
    if (this.crowd) return this.dead && this.crowd.finished;
    return this.dead && (!this.current || !this.current.isRunning());
  }

  /** The soldier falls; returns false for units with no death of their own to play. */
  die(): boolean {
    if (this.crowd) {
      if (!this.crowd.has(CLIP.die)) return false;
      this.dead = true;
      this.crowd.play(CLIP.die, 0.12, true);
      return true;
    }
    const a = this.actions.get(CLIP.die);
    if (!a) return false;
    this.dead = true;
    a.setLoop(THREE.LoopOnce, 1);
    a.clampWhenFinished = true;
    this.play(CLIP.die, 0.12);
    return true;
  }

  /** Advances the model by one frame. While dying, only the death plays on. */
  update(m: MotionInput): void {
    const t = m.now / 1000 + this.phase * 10;
    if (this.crowd) {
      if (!this.dead) {
        if (m.fired) this.kick = 1;
        this.kick *= Math.exp(-m.dt * 16);
        const k = this.type === 'at_team' ? 0.045 : this.type === 'marksman' ? 0.025 : 0.014;
        this.model.userData.recoilKick = this.kick * this.kick * k;
        this.pickClip(m);
      }
      return;
    }
    if (this.mixer) {
      if (!this.dead) this.pickClip(m);
      this.mixer.update(m.dt);
    }
    if (this.dead) return;
    for (const s of this.spinners) s.node.rotation[s.axis] += s.rate * m.dt;
    const accel = (m.speed - this.lastSpeed) / Math.max(m.dt, 1e-3);
    this.lastSpeed = m.speed;
    // lean eases in and out, so a turn or a stop reads as weight shifting
    const ease = 1 - Math.exp(-m.dt * 4);
    const model = this.model;
    switch (this.kind) {
      case 'foot': {
        if (m.fired) this.kick = 1;
        this.kick *= Math.exp(-m.dt * 14);
        const k = this.type === 'at_team' ? 0.085 : this.type === 'marksman' ? 0.05 : 0.03;
        model.position.x = -this.kick * this.kick * k;
        break;
      }
      case 'air': {
        // nose down to fly forward, bank into turns, hover on a slow bob; shudder when firing cannon/rockets
        if (m.fired) { this.kick = 1; this.lastShot = m.now; }
        this.kick *= Math.exp(-m.dt * 12);
        const shudder = this.kick * Math.cos((m.now - this.lastShot) * 0.09);
        this.pitch += (-Math.min(0.28, m.speed * 0.09) - this.pitch) * ease;
        this.bank += (THREE.MathUtils.clamp(-m.turn * 0.5, -0.45, 0.45) - this.bank) * ease;
        model.position.set(-this.kick * 0.04, Math.sin(t * 1.3) * 0.05 + shudder * 0.012, 0);
        model.rotation.set(this.bank + shudder * 0.025, 0, this.pitch + Math.sin(t * 0.9) * 0.02 + this.kick * 0.055);
        break;
      }
      case 'drone': {
        const wing = WINGED.has(this.type);
        this.pitch += (-Math.min(wing ? 0.08 : 0.3, m.speed * 0.1) - this.pitch) * ease;
        this.bank += (THREE.MathUtils.clamp(-m.turn * (wing ? 0.9 : 0.3), -0.7, 0.7) - this.bank) * ease;
        model.position.y = Math.sin(t * (wing ? 1.1 : 2.4)) * (wing ? 0.03 : 0.04);
        model.rotation.set(this.bank + Math.sin(t * 1.7) * 0.03, 0, this.pitch);
        break;
      }
      case 'missile': {
        model.rotation.x += m.dt * 4;
        if (this.flame) {
          const f = 0.8 + 0.35 * Math.sin(t * 47) * Math.sin(t * 29);
          this.flame.scale.set(f, 0.8 + 0.2 * f, 0.8 + 0.2 * f);
        }
        break;
      }
      case 'ship': {
        // naval deck gun slews onto target and kicks back on fire; hull rolls slightly from broadside recoil
        if (this.turret) {
          const want = m.aim ?? (m.now - this.lastShot < 2500 ? this.turret.rotation.y : 0);
          const d = Math.atan2(Math.sin(want - this.turret.rotation.y), Math.cos(want - this.turret.rotation.y));
          this.turret.rotation.y += THREE.MathUtils.clamp(d, -3.2 * m.dt, 3.2 * m.dt);
        }
        if (m.fired) { this.kick = 1; this.lastShot = m.now; }
        this.kick *= Math.exp(-m.dt * 9);
        const gun = this.turret?.rotation.y ?? 0;
        if (this.turret && this.turretBase) {
          const slam = this.kick * this.kick * 0.045;
          this.turret.position.set(this.turretBase.x - slam * Math.cos(gun), this.turretBase.y, this.turretBase.z + slam * Math.sin(gun));
          this.turret.rotation.z = this.kick * 0.065;
        }
        // ride the swell; the bow lifts as it gets going and the stern digs in when it brakes
        const a = this.type === 'destroyer' || this.type === 'drone_ship' ? 0.35 : this.type === 'landing_craft' ? 0.5 : 1;
        this.pitch += (THREE.MathUtils.clamp(m.speed * 0.05 + accel * 0.02, -0.08, 0.14) * a - this.pitch) * ease;
        model.position.y = Math.sin(t * 1.6) * 0.025 * a - this.kick * 0.012;
        model.rotation.set(
          Math.sin(t * 1.1) * 0.06 * a - m.turn * 0.15 * a + this.kick * 0.045 * Math.sin(gun),
          0,
          this.pitch + Math.sin(t * 1.4 + 1) * 0.03 * a + this.kick * 0.035 * Math.cos(gun),
        );
        break;
      }
      case 'vehicle': {
        // the turret swings onto its target; the 125mm barrel slams back on hydro-pneumatic recuperators while the hull rocks on its torsion bars
        if (this.turret) {
          const want = m.aim ?? (m.now - this.lastShot < 2500 ? this.turret.rotation.y : 0);
          const d = Math.atan2(Math.sin(want - this.turret.rotation.y), Math.cos(want - this.turret.rotation.y));
          this.turret.rotation.y += THREE.MathUtils.clamp(d, -2.5 * m.dt, 2.5 * m.dt);
        }
        if (m.fired) { this.kick = 1; this.barrelKick = 1; this.lastShot = m.now; }
        this.kick *= Math.exp(-m.dt * 6.5);
        this.barrelKick *= Math.exp(-m.dt * 5.2);
        this.pitch += (THREE.MathUtils.clamp(-accel * 0.03, -0.05, 0.05) - this.pitch) * ease;
        const rumble = m.speed > 0.05 ? Math.sin(t * 31) * 0.004 : Math.sin(t * 19) * 0.0015;
        const gun = this.turret?.rotation.y ?? 0;
        if (this.barrel && this.barrelBase) {
          const slide = Math.pow(this.barrelKick, 0.65) * 0.14;
          this.barrel.position.set(this.barrelBase.x - slide, this.barrelBase.y, this.barrelBase.z);
          this.barrel.rotation.z = this.barrelKick * 0.045;
        }
        if (this.turret && this.turretBase) {
          const slam = this.kick * this.kick * (this.barrel ? 0.025 : 0.06);
          this.turret.position.set(this.turretBase.x - slam * Math.cos(gun), this.turretBase.y, this.turretBase.z + slam * Math.sin(gun));
          this.turret.rotation.z = this.kick * 0.028;
        }
        const age = Math.max(0, m.now - this.lastShot);
        const rock = this.kick * (Math.cos(age * 0.024) * 0.65 + 0.35);
        model.position.set(-this.kick * 0.065 * Math.cos(gun), rumble - this.kick * 0.014, this.kick * 0.065 * Math.sin(gun));
        model.rotation.set(rock * 0.11 * Math.sin(gun), 0, this.pitch + rock * 0.11 * Math.cos(gun));
        break;
      }
    }
  }

  /** The infantry's clip for what they are doing: aim or shoot, walk or run, or both at once. */
  private pickClip(m: MotionInput): void {
    if (m.fired) this.lastShot = m.now;
    const shooting = m.now - this.lastShot < HOLD_AIM;
    const moving = m.speed > 0.1;
    const name = moving
      ? (shooting ? CLIP.runShoot : m.speed > RUN_FROM ? CLIP.run : CLIP.walk)
      : (shooting ? CLIP.shoot : m.aim !== null ? CLIP.aim : CLIP.idle);
    this.play(name, FADE);
    // the feet keep pace with the ground
    const ref = name === CLIP.walk ? 0.9 : 2;
    const scale = moving ? THREE.MathUtils.clamp(m.speed / ref, 0.6, 1.5) : 1;
    if (this.crowd) this.crowd.setTimeScale(scale);
    else if (this.current) this.current.timeScale = scale;
  }

  private play(name: string, fade: number): void {
    if (this.crowd) {
      if (name === this.crowd.clip) return;
      if (!this.crowd.play(name, fade) && name !== CLIP.idle) this.crowd.play(CLIP.idle, fade);
      return;
    }
    const next = this.actions.get(name) ?? this.actions.get(CLIP.idle);
    if (!next || next === this.current) return;
    next.reset().play();
    if (this.current && fade > 0) this.current.crossFadeTo(next, fade, false);
    else this.current?.stop();
    this.current = next;
  }

  dispose(): void {
    this.crowd?.release();
    // the mixer keeps bindings to every animated node of its root until told to let them go
    if (this.mixer) { this.mixer.stopAllAction(); this.mixer.uncacheRoot(this.mixer.getRoot() as THREE.Object3D); }
    if (this.flame) { this.flame.geometry.dispose(); (this.flame.material as THREE.Material).dispose(); }
  }
}
